import { FetchResult } from '@apollo/client';
import { FormErrorMessage } from '@pendwa/common-browser/components/Form/ErrorMessage/index.js';
import { LayoutSpacerSmall } from '@pendwa/common-browser/components/LayoutSpacerSmall/index.js';
import { LoadingSmall } from '@pendwa/common-browser/components/Loading/LoadingSmall/index.js';
import { extractGraphQLErrorMessage } from '@pendwa/common-browser/core/apollo/utils.js';
import { logger } from '@pendwa/common-browser/core/logger/index.js';
import { rootRoutes } from '@pendwa/common/core/rootRoutes/index.js';
import React from 'react';
import AppleLoginNotDefault from 'react-apple-login';
import { useLocation } from 'react-router-dom';
import { EnumLoginAppleAgent, HooksAccountLoginAppleMutation } from '../../../generated/graphql.js';
import { useAccountLoginAppleGraphql } from '../../../hooks/user/loginAppleGraphql.js';

const AppleLogin = AppleLoginNotDefault as unknown as typeof AppleLoginNotDefault.default;

export const LoginApple: React.FC<{
  handleSuccess: (result: FetchResult<HooksAccountLoginAppleMutation>) => void;
}> = (props) => {
  const [frontend_error_message, setFrontendErrorMessage] = React.useState<string | undefined>('');
  const location = useLocation();
  const { result: mutation_result, doAction } = useAccountLoginAppleGraphql();

  const onFail = (error: any, error_message: string) => {
    logger.error(
      new Error(error_message, {
        cause: error,
      })
    );
    setFrontendErrorMessage(error_message);
  };

  const executeAction = async ({ apple_id_token }: { apple_id_token: string }) => {
    setFrontendErrorMessage(undefined);

    const action_result = await doAction({
      variables: {
        body: {
          id_token: apple_id_token,
          agent: EnumLoginAppleAgent.Web,
        },
      },
    });

    if (action_result.errors) {
      const error_message = extractGraphQLErrorMessage(action_result.errors);
      onFail(error_message, error_message ?? '');
    }

    if (!action_result.errors && action_result.data?.accountLoginApple?.data?.uuid) {
      setTimeout(() => props.handleSuccess(action_result), 500);
    }
  };

  // using redirect, apple returns the id_token in the url hash
  React.useEffect(() => {
    const location_search = location.search;
    if (location_search) {
      try {
        const urlSearchParams = new URLSearchParams(location_search);
        const apple_id_token = urlSearchParams.get('apple_id_token');
        if (apple_id_token) {
          executeAction({ apple_id_token });
        }
      } catch (error) {
        onFail(
          new Error(`failed to parse location search ${location_search}`),
          'Failed to sign you in with Apple. Please try again.'
        );
      }
    }
  }, []);

  return (
    <LayoutSpacerSmall className={'mx-auto max-w-[300px]'}>
      {frontend_error_message && <FormErrorMessage message={frontend_error_message} />}

      {mutation_result.error && (
        <FormErrorMessage
          message={extractGraphQLErrorMessage(mutation_result.error) ?? 'Something went wrong.'}
        />
      )}

      <AppleLogin
        clientId={browserRootConfig.authentication.apple.bundle_id}
        redirectURI={
          new URL(rootRoutes.app.account.loginWithAppleFormPost.get(), browserRootConfig.project.www_url).href
        }
        scope={'email'}
        responseType={'code id_token'}
        responseMode={'form_post'}
        usePopup={false}
        designProp={{
          width: 300,
          height: 45,
          type: 'continue',
        }}
      />

      {mutation_result.loading && <LoadingSmall />}
    </LayoutSpacerSmall>
  );
};
